import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../http';
import './EditEvent.css';
import ConfirmDialog from '../../components/ConfirmDialog';
import EventDocuments from './EventDocuments';

const EditEvent = () => {
  const { id } = useParams();
  // eslint-disable-next-line no-unused-vars
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [stations, setStations] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [proctors, setProctors] = useState([]);
  const [confirm, setConfirm] = useState(null);
  const [requiredStations, setRequiredStations] = useState(9);
  const [savingRequiredStations, setSavingRequiredStations] = useState(false);

  // For Stations
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [stationName, setStationName] = useState('');

  // For Companies
  const [newCompanyName, setNewCompanyName] = useState('');
  const [newCompanyDept, setNewCompanyDept] = useState('');

  // For Students
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [newStudentFirst, setNewStudentFirst] = useState('');
  const [newStudentLast, setNewStudentLast] = useState('');
  const [newStudentCandidateId, setNewStudentCandidateId] = useState('');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = async () => {
    try {
      setLoading(true);
      const [evtRes, stnRes, cmpRes, tplRes, procRes] = await Promise.all([
        api.get(`/api/test-events/${id}`),
        api.get(`/api/test-events/${id}/stations`),
        api.get(`/api/test-events/${id}/companies`),
        api.get(`/api/admin/station-templates`),
        api.get(`/api/admin/proctors`)
      ]);
      setEvent(evtRes.data);
      setRequiredStations(evtRes.data.required_stations ?? 9);
      setStations(stnRes.data);

      // Fetch candidates for each company to display them
      const cRes = await api.get(`/api/test-events/${id}/candidates`);
      const cands = cRes.data;
      const compsWithCands = cmpRes.data.map((c) => ({
        ...c,
        candidates: cands.filter((cand) => cand.company_id === c.id)
      }));

      setCompanies(compsWithCands);
      setTemplates(tplRes.data);
      setProctors(procRes.data);
    } catch (err) {
      console.error(err);
      setError('Failed to load event data. Make sure the event exists.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSaveRequiredStations = async (overrideVal) => {
    const val = overrideVal !== undefined ? overrideVal : parseInt(requiredStations, 10);
    if (isNaN(val) || val < 1) return;
    try {
      setSavingRequiredStations(true);
      await api.put(`/api/test-events/${id}`, { required_stations: val });
      setRequiredStations(val);
      setEvent((prev) => ({ ...prev, required_stations: val }));
    } catch (err) {
      alert('Error saving required stations: ' + (err.response?.data?.error || err.message));
    } finally {
      setSavingRequiredStations(false);
    }
  };

  const handleAddStation = async () => {
    if (!selectedTemplate && !stationName) return;
    try {
      const payload = {
        station_number: stations.length + 1
      };
      if (selectedTemplate) payload.station_template_id = parseInt(selectedTemplate, 10);
      if (stationName) payload.name = stationName;

      await api.post(`/api/test-events/${id}/stations`, payload);
      setSelectedTemplate('');
      setStationName('');
      fetchData();
    } catch (err) {
      alert('Error adding station: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDeleteStation = (stationId) => {
    setConfirm({
      message: 'Are you sure you want to delete this station?',
      confirmLabel: 'Delete',
      destructive: true,
      onConfirm: async () => {
        try {
          await api.delete(`/api/test-events/${id}/stations/${stationId}`);
          setStations((prev) => prev.filter(s => s.id !== stationId));
        } catch (err) {
          alert('Error deleting station: ' + (err.response?.data?.error || err.message));
        }
      }
    });
  };

  const handleUpdateStationProctor = async (stationId, proctorId) => {
    try {
      await api.put(`/api/test-events/${id}/stations/${stationId}/proctor`, { proctor_id: proctorId });
      setStations((prev) =>
        prev.map(s => s.id === stationId ? { ...s, proctor_user_id: proctorId ? parseInt(proctorId, 10) : null } : s)
      );
    } catch (err) {
      alert('Error updating proctor: ' + (err.response?.data?.error || err.message));
      fetchData(); // Rollback on error
    }
  };

  const handleAddCompany = async () => {
    if (!newCompanyName) return;
    try {
      await api.post(`/api/test-events/${id}/companies`, {
        name: newCompanyName,
        home_department: newCompanyDept,
        candidates: []
      });
      setNewCompanyName('');
      setNewCompanyDept('');
      fetchData();
    } catch (err) {
      alert('Error adding company: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDeleteCompany = (companyId) => {
    setConfirm({
      message: 'Delete this company and ALL its registered students?',
      confirmLabel: 'Delete',
      destructive: true,
      onConfirm: async () => {
        try {
          await api.delete(`/api/test-events/${id}/companies/${companyId}`);
          setCompanies((prev) => prev.filter(c => c.id !== companyId));
        } catch (err) {
          alert('Error deleting company: ' + (err.response?.data?.error || err.message));
        }
      }
    });
  };

  const handleAddStudent = async (companyId) => {
    if (!newStudentFirst || !newStudentLast) return;
    try {
      await api.post(`/api/test-events/${id}/companies/${companyId}/candidates`, {
        first_name: newStudentFirst,
        last_name: newStudentLast,
        candidate_id: newStudentCandidateId
      });
      setNewStudentFirst('');
      setNewStudentLast('');
      setNewStudentCandidateId('');
      setSelectedCompanyId('');
      fetchData();
    } catch (err) {
      alert('Error adding student: ' + (err.response?.data?.error || err.message));
    }
  };

  if (loading)
    return (
      <div className="edit-event-container">
        <p>Loading Event Data...</p>
      </div>
    );
  if (error)
    return (
      <div className="edit-event-container">
        <p className="text-danger">{error}</p>
      </div>
    );
  if (!event)
    return (
      <div className="edit-event-container">
        <p>Event not found.</p>
      </div>
    );

  return (
    <div className="edit-event-container">
      <ConfirmDialog confirm={confirm} onCancel={() => setConfirm(null)} />
      <header className="edit-event-header">
        <div>
          <h2>{event.name}</h2>
          <p style={{ margin: '0.5rem 0 0 0', color: '#718096' }}>
            Event Date: {new Date(event.event_date).toLocaleDateString()} | Location:{' '}
            {event.location || 'N/A'} | Status: {event.status}
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
            <label style={{ fontWeight: 600, fontSize: '0.9em', color: '#4a5568' }}>
              Required Stations to Complete:
            </label>
            <input
              type="number"
              min="1"
              max="99"
              value={requiredStations}
              onChange={(e) => setRequiredStations(e.target.value)}
              className="input input-sm"
              style={{ width: '70px' }}
            />
            <button
              className="btn btn-primary btn-sm"
              onClick={handleSaveRequiredStations}
              disabled={savingRequiredStations}
            >
              {savingRequiredStations ? 'Saving…' : 'Save'}
            </button>
            {stations.length > 0 && parseInt(requiredStations) !== stations.length && (
              <span style={{ fontSize: '0.85em', color: '#c05621', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                ⚠ You have {stations.length} station{stations.length !== 1 ? 's' : ''} but require {requiredStations}.
                <button
                  className="btn btn-sm"
                  style={{ background: '#ed8936', color: 'white', padding: '2px 8px' }}
                  onClick={() => handleSaveRequiredStations(stations.length)}
                >
                  Set to {stations.length}
                </button>
              </span>
            )}
          </div>
        </div>
        <Link to="/admin" className="btn btn-secondary">
          Back to Admin
        </Link>
      </header>

      <div className="edit-grid">
        {/* Stations Column */}
        <div className="edit-card">
          <h3>Stations ({stations.length})</h3>
          <p className="text-muted" style={{ marginBottom: '1rem' }}>
            Add stations from templates or enter a custom name.
          </p>
          <div className="add-form">
            <select
              value={selectedTemplate}
              onChange={(e) => setSelectedTemplate(e.target.value)}
              className="input"
              style={{ flex: 2 }}
            >
              <option value="">-- Template (Optional) --</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
            <input
              type="text"
              className="input"
              placeholder="Station Name"
              value={stationName}
              onChange={(e) => setStationName(e.target.value)}
              style={{ flex: 1.5 }}
            />
            <button
              className="btn btn-primary"
              onClick={handleAddStation}
              disabled={!selectedTemplate && !stationName}
            >
              Add
            </button>
          </div>
          <ul className="item-list">
            {stations.map((s) => (
              <li key={s.id} className="item-row" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
                <div style={{ flex: '1 1 auto' }}>
                  <strong>Station {s.station_number}:</strong> {s.name}
                  <span style={{ color: '#718096', fontSize: '0.9em', marginLeft: '0.5rem' }}>
                    (PIN: {s.station_pin || 'N/A'})
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <select
                    className="input input-sm"
                    value={s.proctor_user_id || ''}
                    onChange={(e) => handleUpdateStationProctor(s.id, e.target.value)}
                    style={{ width: '150px' }}
                  >
                    <option value="">-- No Proctor --</option>
                    {proctors.filter(p => p.is_active).map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.first_name} {p.last_name}
                      </option>
                    ))}
                  </select>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDeleteStation(s.id)}>
                    Remove
                  </button>
                </div>
              </li>
            ))}
            {stations.length === 0 && (
              <p className="text-muted" style={{ marginTop: '1rem' }}>
                No stations added yet.
              </p>
            )}
          </ul>
        </div>

        {/* Companies/Candidates Column */}
        <div className="edit-card">
          <h3>Companies & Students</h3>
          <p className="text-muted" style={{ marginBottom: '1rem' }}>
            Register companies/departments, then add students into them.
          </p>
          <div className="add-form multi">
            <input
              type="text"
              placeholder="Company/Dept Name *"
              className="input"
              value={newCompanyName}
              onChange={(e) => setNewCompanyName(e.target.value)}
            />
            <input
              type="text"
              placeholder="Department ID (Optional)"
              className="input"
              value={newCompanyDept}
              onChange={(e) => setNewCompanyDept(e.target.value)}
            />
            <button
              className="btn btn-primary btn-sm"
              onClick={handleAddCompany}
              disabled={!newCompanyName}
            >
              + Company
            </button>
          </div>

          <div className="company-list">
            {companies.map((c) => (
              <div key={c.id} className="company-block">
                <div className="company-header">
                  <h4 style={{ margin: 0, color: '#2b6cb0' }}>
                    {c.name}{' '}
                    {c.home_department && (
                      <span style={{ color: '#718096', fontSize: '0.9em' }}>
                        ({c.home_department})
                      </span>
                    )}
                  </h4>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleDeleteCompany(c.id)}
                  >
                    Delete
                  </button>
                </div>

                <ul className="student-list">
                  {c.candidates &&
                    c.candidates.map((cand) => (
                      <li key={cand.id} className="student-row">
                        {cand.first_name} {cand.last_name}{' '}
                        {cand.candidate_id ? (
                          <span className="text-muted">ID: {cand.candidate_id}</span>
                        ) : (
                          ''
                        )}
                      </li>
                    ))}
                  {(!c.candidates || c.candidates.length === 0) && (
                    <li className="text-muted" style={{ padding: '0.5rem' }}>
                      No students registered.
                    </li>
                  )}
                </ul>

                {/* Add Student Inline */}
                {selectedCompanyId === c.id ? (
                  <div className="add-student-form">
                    <input
                      type="text"
                      placeholder="First Name *"
                      className="input input-sm"
                      value={newStudentFirst}
                      onChange={(e) => setNewStudentFirst(e.target.value)}
                    />
                    <input
                      type="text"
                      placeholder="Last Name *"
                      className="input input-sm"
                      value={newStudentLast}
                      onChange={(e) => setNewStudentLast(e.target.value)}
                    />
                    <input
                      type="text"
                      placeholder="ID (Opt)"
                      className="input input-sm"
                      value={newStudentCandidateId}
                      onChange={(e) => setNewStudentCandidateId(e.target.value)}
                      style={{ width: '80px', flex: 'none' }}
                    />
                    <button
                      className="btn btn-success btn-sm"
                      disabled={!newStudentFirst || !newStudentLast}
                      onClick={() => handleAddStudent(c.id)}
                    >
                      Save
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setSelectedCompanyId('')}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    className="btn btn-secondary btn-sm mt-2"
                    onClick={() => {
                      setSelectedCompanyId(c.id);
                      setNewStudentFirst('');
                      setNewStudentLast('');
                      setNewStudentCandidateId('');
                    }}
                  >
                    + Add Student
                  </button>
                )}
              </div>
            ))}
            {companies.length === 0 && (
              <p className="text-muted" style={{ marginTop: '1rem' }}>
                No companies registered yet.
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="edit-grid" style={{ marginTop: '1.5rem' }}>
        <div className="edit-card" style={{ gridColumn: '1 / -1' }}>
          <EventDocuments eventId={id} />
        </div>
      </div>
    </div>
  );
};

export default EditEvent;
